// frontend/src/components/Header.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import UserRoleIndicator from "./UserRoleIndicator";
import UserDropdown from "./UserDropdown";
import DeveloperDropdown from "./DeveloperDropdown";
import BackendSwitcher from "./BackendSwitcher";
import { useAuth } from "../contexts/AuthContext.jsx";
import { isLocal, isPreview, togglePreviewMode } from "../config/api.js";
import { useHomePageHeader } from "../contexts/HomePageHeaderContext.jsx";
import SortDropdown from "./navigation/SortDropdown";

const Header = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, canAccessAdmin, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isLandscapeMobile, setIsLandscapeMobile] = useState(false);

  // Get homepage header data from context (only available on homepage)
  const homePageHeaderData = useHomePageHeader();

  // Detect landscape mobile orientation
  useEffect(() => {
    const checkLandscapeMobile = () => {
      const isMobile = window.innerWidth < 640;
      const isLandscape = window.matchMedia("(orientation: landscape)").matches;
      // Check if we're on homepage (with or without query params)
      const isHomePage = location.pathname === "/" || location.pathname === "";
      const shouldShow =
        isMobile && isLandscape && isHomePage && homePageHeaderData;

      // Debug logging (remove in production)
      if (import.meta.env.DEV) {
        console.log("🔍 Header Landscape Mobile Check:", {
          isMobile,
          isLandscape,
          isHomePage,
          pathname: location.pathname,
          hasContext: !!homePageHeaderData,
          hasCategories: !!homePageHeaderData?.categories?.length,
          shouldShow,
        });
      }

      setIsLandscapeMobile(shouldShow);
    };

    checkLandscapeMobile();
    window.addEventListener("resize", checkLandscapeMobile);
    window.addEventListener("orientationchange", checkLandscapeMobile);

    return () => {
      window.removeEventListener("resize", checkLandscapeMobile);
      window.removeEventListener("orientationchange", checkLandscapeMobile);
    };
  }, [location.pathname, homePageHeaderData]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);

    // Update URL with new language parameter
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("lang", lng);

    // Navigate to the same page with updated language parameter
    const newPath = currentUrl.pathname + currentUrl.search;
    navigate(newPath, { replace: true });
  };

  const currentLanguage = i18n.language;

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isLanguageDropdownOpen &&
        !event.target.closest(".language-dropdown")
      ) {
        setIsLanguageDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isLanguageDropdownOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(20);
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Category icons (same as StickyCategoryBar; kids = merged kids directory)
  const categoryIcons = {
    men: "👔",
    ladies: "👗",
    baby: "👶",
    kids: "🧒",
    "kids2-8": "🧒",
    "kids9-14": "👦",
    home: "🏠",
  };

  // Calculate active filter count
  const activeFilterCount = homePageHeaderData
    ? (homePageHeaderData.filters?.category !== "all" ? 1 : 0) +
      (homePageHeaderData.filters?.sortBy !== "freshness_desc" ? 1 : 0) +
      (homePageHeaderData.filters?.minDiscount > 0 ? 1 : 0)
    : 0;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 bg-white text-gray-900 z-40 border-b border-gray-200 ${
          isLandscapeMobile && homePageHeaderData
            ? "px-2 py-1.5 min-h-[44px] shadow-none"
            : "px-3 py-2 min-h-[44px] sm:min-h-[48px] shadow-sm"
        }`}
      >
        <div
          className={`container mx-auto flex ${
            isLandscapeMobile && homePageHeaderData
              ? "items-center gap-2"
              : "justify-between items-center"
          }`}
        >
          {/* Logo - Compact in landscape mobile and mobile portrait */}
          <Link
            to={`/?lang=${currentLanguage}`}
            className={`font-semibold hover:text-gray-600 flex-shrink-0 flex items-center gap-1.5 ${
              isLandscapeMobile && homePageHeaderData
                ? "text-sm"
                : "text-sm sm:text-base"
            }`}
            onClick={closeMobileMenu}
          >
            <img
              src="/logo.png?v=5"
              alt=""
              className="h-7 w-auto sm:h-8"
              width="160"
              height="32"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/logo.svg";
              }}
            />
          </Link>

          {/* Merged Navigation - Landscape Mobile Only */}
          {isLandscapeMobile &&
            homePageHeaderData &&
            homePageHeaderData.categories &&
            homePageHeaderData.categories.length > 0 && (
              <div className="flex-1 flex items-center gap-2 mx-2 overflow-hidden">
                {/* Categories - Horizontal Scroll */}
                <div className="flex-1 overflow-x-auto scrollbar-hide">
                  <div className="flex items-center gap-1.5">
                    {homePageHeaderData.categories.map((category) => {
                      const icon = categoryIcons[category.key] || "🏷️";
                      const isActive =
                        homePageHeaderData.filters?.category &&
                        homePageHeaderData.filters.category !== "all" &&
                        (homePageHeaderData.filters.category === category.key ||
                          homePageHeaderData.filters.category ===
                            category.primaryVariation);

                      return (
                        <button
                          key={category.key}
                          onClick={() =>
                            homePageHeaderData.onCategoryClick?.(category.key)
                          }
                          className={`
                          flex items-center gap-1 px-2 py-1 rounded-full
                          text-xs font-medium transition-all duration-200
                          whitespace-nowrap flex-shrink-0
                          touch-target min-w-[36px] min-h-[36px]
                          ${
                            isActive
                              ? "bg-blue-600 text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }
                        `}
                          aria-label={t(
                            "categories." + category.key,
                            category.name,
                          )}
                          aria-pressed={isActive}
                        >
                          <span className="text-base">{icon}</span>
                          <span className="text-xs font-medium truncate max-w-[4rem] sm:max-w-none">
                            {t("categories." + category.key, category.name)}
                          </span>
                          {category.count !== undefined && (
                            <span
                              className={`text-[10px] font-bold ${
                                (category.count || 0) > 0
                                  ? "text-blue-600"
                                  : "text-gray-400"
                              }`}
                            >
                              {category.count}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Filter Button - Icon Only */}
                <button
                  onClick={() => homePageHeaderData.onOpenMobileSheet?.()}
                  className="relative p-1.5 rounded-md bg-transparent hover:bg-gray-100 transition-colors touch-target min-w-[36px] min-h-[36px] flex items-center justify-center"
                  aria-label={t("filter.title", "Filter")}
                >
                  <svg
                    className="w-5 h-5 text-gray-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                    />
                  </svg>
                  {activeFilterCount > 0 && (
                    <span className="absolute -top-1 -right-1 px-1 py-0.5 rounded-full text-[10px] font-bold bg-orange-500 text-white">
                      {activeFilterCount}
                    </span>
                  )}
                </button>

                {/* Sort Dropdown - Compact */}
                <div className="flex-shrink-0">
                  <SortDropdown
                    value={
                      homePageHeaderData.filters?.sortBy || "freshness_desc"
                    }
                    onChange={(value) => {
                      homePageHeaderData.onFilterChange?.({ sortBy: value });
                      // Scroll to top on sort change
                      setTimeout(() => {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }, 100);
                    }}
                    compact={true}
                    showDirection={true}
                  />
                </div>

                {/* Auth Button - Compact (Unified Auth Flow) */}
                <div className="flex-shrink-0 flex items-center gap-1">
                  {isAuthenticated ? (
                    <UserDropdown />
                  ) : (
                    <Link
                      to={`/auth?lang=${currentLanguage}`}
                      className="px-2 py-1 text-xs font-medium bg-white border border-gray-300 text-gray-900 hover:bg-gray-50 hover:border-gray-400 rounded transition-colors touch-target min-w-[44px] min-h-[44px] flex items-center justify-center"
                      aria-label={t("login", "Log in")}
                    >
                      <span className="text-sm">🔑</span>
                    </Link>
                  )}
                </div>
              </div>
            )}

          {/* Desktop Navigation - Hidden in landscape mobile when merged nav is shown */}
          <nav
            className={`${
              isLandscapeMobile && homePageHeaderData
                ? "hidden"
                : "hidden md:flex"
            } items-center gap-3 ${
              currentLanguage === "he" ? "space-x-reverse" : ""
            }`}
          >
            {/* Dashboard Link - Only show when authenticated */}
            {isAuthenticated && (
              <Link
                to={`/dashboard?lang=${currentLanguage}`}
                className="px-3 py-2 h-9 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200 flex items-center gap-2 whitespace-nowrap"
              >
                <span>📊</span>
                <span>{t("dashboard")}</span>
              </Link>
            )}

            {/* Compact User Role Indicator - Only show when authenticated */}
            {isAuthenticated && (
              <UserRoleIndicator showDetails={false} compact={true} />
            )}

            {/* Unified Auth Link - Only show when not authenticated (Unified Auth Flow) */}
            {!isAuthenticated && (
              <Link
                to={`/auth?lang=${currentLanguage}`}
                className={`px-3 py-2 text-base font-medium bg-white border-2 border-gray-300 text-gray-900 hover:bg-gray-50 hover:border-gray-400 rounded-lg transition-all duration-200 hover:shadow-md flex items-center gap-2 whitespace-nowrap ${
                  currentLanguage === "he" ? "flex-row-reverse" : ""
                }`}
              >
                <span className="font-semibold">{t("login", "Log in")}</span>
              </Link>
            )}

            {/* Developer Dropdown */}
            <DeveloperDropdown />

            {/* Language Switcher */}
            <div className="relative language-dropdown">
              <button
                onClick={() =>
                  setIsLanguageDropdownOpen(!isLanguageDropdownOpen)
                }
                className={`px-3 py-2 h-9 text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all duration-200 flex items-center gap-2 whitespace-nowrap ${
                  currentLanguage === "he" ? "flex-row-reverse" : ""
                }`}
                aria-label="Select language"
              >
                <span className="text-base">
                  {currentLanguage === "en" ? "🇺🇸" : "🇮🇱"}
                </span>
                <span className="font-semibold">
                  {currentLanguage === "en" ? "EN" : "עברית"}
                </span>
                <span
                  className={`text-xs transition-transform duration-200 ${
                    isLanguageDropdownOpen ? "rotate-180" : ""
                  }`}
                >
                  ▼
                </span>
              </button>

              {/* Language Dropdown */}
              {isLanguageDropdownOpen && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-32">
                  <button
                    onClick={() => {
                      changeLanguage("en");
                      setIsLanguageDropdownOpen(false);
                    }}
                    className={`w-full flex items-center space-x-2 px-3 py-2 text-sm transition-colors ${
                      currentLanguage === "en"
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span className="text-base">🇺🇸</span>
                    <span>English</span>
                  </button>
                  <button
                    onClick={() => {
                      changeLanguage("he");
                      setIsLanguageDropdownOpen(false);
                    }}
                    className={`w-full flex items-center space-x-2 px-3 py-2 text-sm transition-colors ${
                      currentLanguage === "he"
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span className="text-base">🇮🇱</span>
                    <span>עברית</span>
                  </button>
                </div>
              )}
            </div>

            {/* User Dropdown - Only show when authenticated */}
            {isAuthenticated && <UserDropdown />}
          </nav>

          {/* Mobile Hamburger Menu Button - Hidden in landscape mobile when merged nav is shown */}
          <button
            onClick={toggleMobileMenu}
            className={`${
              isLandscapeMobile && homePageHeaderData ? "hidden" : "md:hidden"
            } p-1.5 rounded-md bg-transparent hover:bg-gray-100 transition-colors touch-target`}
            aria-label="Toggle mobile menu"
          >
            <div className="w-5 h-5 flex flex-col justify-center items-center">
              <span
                className={`block w-4 h-0.5 bg-gray-700 transition-all duration-300 ${
                  isMobileMenuOpen ? "rotate-45 translate-y-1" : ""
                }`}
              />
              <span
                className={`block w-4 h-0.5 bg-gray-700 transition-all duration-300 mt-1 ${
                  isMobileMenuOpen ? "opacity-0" : ""
                }`}
              />
              <span
                className={`block w-4 h-0.5 bg-gray-700 transition-all duration-300 mt-1 ${
                  isMobileMenuOpen ? "-rotate-45 -translate-y-1" : ""
                }`}
              />
            </div>
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile Menu Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out md:hidden ${
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Mobile Menu Header */}
        <div className="bg-blue-500 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-xl">👤</span>
              </div>
              <div>
                <h3 className="font-semibold">
                  {isAuthenticated ? user?.email || t("user") : t("guest")}
                </h3>
                <p className="text-sm opacity-90">
                  {isAuthenticated
                    ? `${t("level")} ${user?.role_level || 1}`
                    : t("not_logged_in")}
                </p>
              </div>
            </div>
            <button
              onClick={closeMobileMenu}
              className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors touch-target"
            >
              <span className="text-xl">✕</span>
            </button>
          </div>
        </div>

        {/* Mobile Menu Content */}
        <div className="p-4 space-y-2">
          {/* Dashboard Link */}
          {isAuthenticated && (
            <Link
              to={`/dashboard?lang=${currentLanguage}`}
              onClick={closeMobileMenu}
              className="flex items-center space-x-3 p-4 rounded-xl bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors touch-target"
            >
              <span className="text-xl">📊</span>
              <span className="font-semibold">{t("dashboard")}</span>
            </Link>
          )}

          {/* Unified Auth Link (Unified Auth Flow) */}
          {!isAuthenticated && (
            <Link
              to={`/auth?lang=${currentLanguage}`}
              onClick={closeMobileMenu}
              className="flex items-center space-x-2 p-4 rounded-xl bg-white border-2 border-gray-300 text-gray-900 hover:bg-gray-50 hover:border-gray-400 transition-colors touch-target"
            >
              <span className="font-semibold">{t("login", "Log in")}</span>
            </Link>
          )}

          {/* Language Switcher */}
          <div className="border-t border-gray-200 pt-4 mt-4">
            <h4 className="text-sm font-semibold text-gray-600 mb-3">
              {t("auth.language")}
            </h4>
            <div className="space-y-2">
              <button
                onClick={() => {
                  changeLanguage("en");
                  closeMobileMenu();
                }}
                className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors touch-target ${
                  currentLanguage === "en"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                }`}
              >
                <span className="text-lg">🇺🇸</span>
                <span>English</span>
              </button>
              <button
                onClick={() => {
                  changeLanguage("he");
                  closeMobileMenu();
                }}
                className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors touch-target ${
                  currentLanguage === "he"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                }`}
              >
                <span className="text-lg">🇮🇱</span>
                <span>עברית</span>
              </button>
            </div>
          </div>

          {/* Development Tools (Admin Only) */}
          {isAuthenticated && canAccessAdmin && canAccessAdmin() && (
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h4 className="text-sm font-semibold text-gray-600 mb-3">
                Development
              </h4>
              <div className="space-y-2">
                <div className="p-3 rounded-lg bg-gray-50">
                  <BackendSwitcher />
                </div>
                {/* Scraping Health Dashboard Link */}
                <button
                  onClick={() => {
                    navigate("/admin/scraping-health");
                    closeMobileMenu();
                  }}
                  className="w-full flex items-center space-x-3 p-3 rounded-lg transition-colors touch-target bg-gray-100 text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                >
                  <span className="text-lg">🏥</span>
                  <span>Scraping Health</span>
                </button>
                {isLocal && (
                  <button
                    onClick={() => {
                      togglePreviewMode();
                      closeMobileMenu();
                    }}
                    className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors touch-target ${
                      isPreview
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    <span className="text-lg">👁️</span>
                    <span>
                      {isPreview ? "Development Mode" : "Preview Mode"}
                    </span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Header;
